import { useEffect, useId, useRef, useState } from 'react'
import { TextLink } from '@indipia/ui-core'
import type { ConfirmProgressStep } from '../constants/partnerStatus'

// 시공파트너 카드 진행 요약 — 한 줄 요약("진행 n/N · 현재 단계명")을 누르면 단계 목록이 펼쳐진다.
// 단계 배열을 입력으로 받는다(일정확인 3단계 외에 작업요청·작업보고 단계를 뒤에 이어 붙일 수 있게).
// detailKey 가 있는 단계는 줄을 눌러 세부 내역 팝업을 연다(onOpenDetail, 줄 끝 '›').
// 공종을 바꾸면 접히도록 부모가 key 로 공종을 구분해 마운트한다.

interface Props {
  steps: ConfirmProgressStep[]
  /** 처음부터 펼친 채로 마운트(딥링크 card=pt·sup). 마운트 때만 읽음 — 기본 false. 켜면 마운트 때 카드를 화면에 보인다 */
  defaultOpen?: boolean
  /** 세부 내역이 있는 단계(detailKey)의 줄을 눌렀을 때 */
  onOpenDetail?: (detailKey: string) => void
  /** 단계 이동 링크(action)를 눌렀을 때 — 없으면 링크를 그리지 않는다 */
  onAction?: (actionKey: string) => void
}

const DOT: Record<ConfirmProgressStep['state'], string> = {
  done:    'var(--status-success-500)',
  current: 'var(--status-warning-500)',
  pending: 'transparent',
}

const label12: React.CSSProperties = { fontSize: 12, color: 'var(--shell-on-surface-2)' }

export function ConfirmProgressSummary({ steps, defaultOpen = false, onOpenDetail, onAction }: Props) {
  const [open, setOpen] = useState(defaultOpen)
  const listId = useId()
  const rootRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (defaultOpen) rootRef.current?.scrollIntoView({ block: 'nearest' })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  if (steps.length === 0) return null
  // 지금 단계가 없으면(모두 끝남) 마지막으로 끝난 단계를 보인다
  const idx = steps.findIndex(s => s.state === 'current')
  let lastDone = -1
  steps.forEach((s, i) => { if (s.state === 'done') lastDone = i })
  const curIdx = idx >= 0 ? idx : Math.max(lastDone, 0)
  const cur = steps[curIdx]

  return (
    <div ref={rootRef} style={{ borderTop: 'var(--border-width-hairline) solid var(--shell-border)', paddingTop: 6 }}>
      <button
        type="button"
        onMouseDown={e => e.stopPropagation()}
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
        aria-controls={listId}
        style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left', minWidth: 0 }}
      >
        <span aria-hidden="true" style={{ fontSize: 10, color: 'var(--shell-on-surface-2)', flexShrink: 0 }}>{open ? '▾' : '▸'}</span>
        {/* 접힌 줄은 '진행 n/N · 단계명'만 — 지금 상태 문구는 카드 상태 줄이 이미 보여주므로 되풀이하지 않는다(펼치면 단계별 문구) */}
        <span style={{ ...label12, flexShrink: 0 }}>{`진행 ${curIdx + 1}/${steps.length}`}</span>
        <b style={{ fontSize: 12, color: 'var(--shell-on-surface)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cur.title}</b>
      </button>
      {open && (
        <ol id={listId} style={{ listStyle: 'none', margin: '6px 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {steps.map((s, i) => (
            <li key={s.title} style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
              <span
                aria-hidden="true"
                style={{
                  width: 8, height: 8, borderRadius: '50%', flexShrink: 0, boxSizing: 'border-box', marginTop: 4,
                  background: DOT[s.state],
                  border: s.state === 'pending' ? '1.5px solid var(--shell-hint)' : 'none',
                }}
              />
              <div style={{ minWidth: 0 }}>
                <span style={{ fontSize: 12, fontWeight: s.state === 'current' ? 700 : 500, color: 'var(--shell-on-surface)' }}>{`${i + 1}. ${s.title}`}</span>
                {s.detailKey && onOpenDetail ? (
                  <TextLink
                    style={{ ...label12, marginLeft: 6 }}
                    title="세부 내역 보기"
                    onMouseDown={e => e.stopPropagation()}
                    onClick={() => onOpenDetail(s.detailKey as string)}
                  >{`${s.text} ›`}</TextLink>
                ) : (
                  <span style={{ ...label12, marginLeft: 6 }}>{s.text}</span>
                )}
                {s.action && onAction && (
                  <TextLink
                    style={{ fontSize: 12, marginLeft: 8 }}
                    onMouseDown={e => e.stopPropagation()}
                    onClick={() => onAction(s.action!.key)}
                  >{`${s.action.label} ›`}</TextLink>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  )
}
